import React, { useState } from "react";
import { Droplets, Wind, Waves } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { buildHourlyData } from "./useMarineWeather";
import { cn } from "@/lib/utils";
import { getPublicWeatherTranslations } from "@/i18n";

const CHART_PERIODS = [
  { label: "12h", hours: 12 },
  { label: "24h", hours: 24 },
  { label: "48h", hours: 48 },
  { label: "7d",  hours: 168 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl p-3 shadow-xl text-xs space-y-1 min-w-[140px]">
      <p className="font-semibold text-foreground mb-2">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex justify-between gap-4">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="font-bold text-foreground">{p.value} {p.unit || ""}</span>
        </div>
      ))}
    </div>
  );
};

export default function MarineCharts({ marine, forecast, lang = "en" }) {
  const [period, setPeriod] = useState(24);
  const copy = getPublicWeatherTranslations(lang).trends;

  const data = buildHourlyData(marine, forecast, Math.min(period, (marine?.hourly?.time?.length ?? 24)));
  const historicalData = React.useMemo(() => {
    const times = forecast?.hourly?.time || [];
    const now = Date.now();
    return times
      .map((time, index) => ({
        timestamp: new Date(time).getTime(),
        time: new Date(time).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit" }),
        windSpeed: Math.round(forecast?.hourly?.wind_speed_10m?.[index] ?? 0),
        pressure: Math.round(forecast?.hourly?.pressure_msl?.[index] ?? 0),
        humidity: Math.round(forecast?.hourly?.relative_humidity_2m?.[index] ?? 0),
      }))
      .filter((item) => item.timestamp <= now)
      .slice(-Math.min(period, 48));
  }, [forecast, period]);

  // Thin out labels for 7d
  const tickInterval = period <= 24 ? 2 : period <= 48 ? 4 : 12;

  return (
    <div className="space-y-4">
      {/* Period selector */}
      <div className="flex gap-2 flex-wrap">
        {CHART_PERIODS.map(p => (
          <Button
            key={p.hours}
            size="sm"
            variant={period === p.hours ? "default" : "outline"}
            onClick={() => setPeriod(p.hours)}
            className="text-xs"
          >
            {p.label}
          </Button>
        ))}
      </div>

      <div>
        <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{copy.eyebrow}</p>
        <h3 className="mt-2 font-heading text-2xl font-black text-foreground">{copy.title}</h3>
        <p className="mt-2 max-w-3xl text-sm font-semibold text-muted-foreground">{copy.intro}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Wind Speed & Gusts */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-heading uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Wind className="w-4 h-4 text-cyan-400" /> {copy.windGusts}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="windGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#22d3ee" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="gustGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#f97316" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={tickInterval} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" kt" width={42} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={15} stroke="#f97316" strokeDasharray="4 4" strokeOpacity={0.5} label={{ value: copy.caution15, fill: "#f97316", fontSize: 10 }} />
                <ReferenceLine y={25} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.5} label={{ value: copy.warning25, fill: "#ef4444", fontSize: 10 }} />
                <Area type="monotone" dataKey="windGusts"  name={copy.gusts}      stroke="#f97316" fill="url(#gustGrad)" unit="kt" strokeWidth={1.5} dot={false} />
                <Area type="monotone" dataKey="windSpeed"  name={copy.windSpeed} stroke="#22d3ee" fill="url(#windGrad)" unit="kt" strokeWidth={2}   dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Wave Height & Swell */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-heading uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Waves className="w-4 h-4 text-blue-400" /> {copy.waveSwell}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="waveGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#60a5fa" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#60a5fa" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="swellGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#818cf8" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#818cf8" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={tickInterval} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit="m" width={36} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={1.5} stroke="#f97316" strokeDasharray="4 4" strokeOpacity={0.5} label={{ value: copy.moderate15m, fill: "#f97316", fontSize: 10 }} />
                <ReferenceLine y={2.5} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.5} label={{ value: copy.rough25m, fill: "#ef4444", fontSize: 10 }} />
                <Area type="monotone" dataKey="swellHeight" name={copy.swell}      stroke="#818cf8" fill="url(#swellGrad)" unit="m" strokeWidth={1.5} dot={false} />
                <Area type="monotone" dataKey="waveHeight"  name={copy.waveHeight} stroke="#60a5fa" fill="url(#waveGrad)"  unit="m" strokeWidth={2}   dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-heading uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Wind className="w-4 h-4 text-cyan-400" /> {copy.historicalWind}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={historicalData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={tickInterval} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" kt" width={42} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={15} stroke="#f97316" strokeDasharray="4 4" strokeOpacity={0.45} />
                <Line type="monotone" dataKey="windSpeed" name={copy.pastWindSpeed} stroke="#22d3ee" unit="kt" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-heading uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Droplets className="w-4 h-4 text-blue-500" /> {copy.humidityTrend}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={historicalData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={tickInterval} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit="%" width={42} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={80} stroke="#2563eb" strokeDasharray="4 4" strokeOpacity={0.45} label={{ value: copy.humid80, fill: "#2563eb", fontSize: 10 }} />
                <Line type="monotone" dataKey="humidity" name={copy.relativeHumidity} stroke="#2563eb" unit="%" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}